import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Dialog } from "@mui/material";
import { GoogleMap, OverlayView, OverlayViewF, PolylineF, useJsApiLoader } from "@react-google-maps/api";
import { dt } from "../../../../shared/themes/redesignTokens";
import { GOOGLE_MAPS_LIBRARIES, GOOGLE_MAPS_SCRIPT_ID } from "../../../../shared/config/googleMapsConfig";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { computeDistanceKm } from "../../../../shared/utils/geoUtils";
import HomeMarker from "../../../serviceBoard/components/map/HomeMarker";
import RfsOwnerMarker from "../../../servicing/components/rfsDetail/RfsOwnerMarker";

// ===================================================================
//  TYPES
// ===================================================================

export interface OfferMapData {
  counterpartLat: number;
  counterpartLng: number;
  counterpartName: string;
  counterpartLogoUrl?: string;
  counterpartCity?: string;
  offerNumber: string;
}

export interface ViewerLocation {
  lat: number;
  lng: number;
}

interface OfferMapModalProps {
  open: boolean;
  onClose: () => void;
  mapData: OfferMapData;
  viewerLocation: ViewerLocation | null;
}

// ===================================================================
//  KEYFRAMES
// ===================================================================

const DIALOG_KEYFRAMES_ID = "offer-map-dialog-kf";
const injectDialogKeyframes = () => {
  if (document.getElementById(DIALOG_KEYFRAMES_ID)) return;
  const style = document.createElement("style");
  style.id = DIALOG_KEYFRAMES_ID;
  style.textContent = `
    @keyframes offerMapDialogEnter {
      0%   { opacity: 0; transform: scale(0.96) translateY(8px); }
      100% { opacity: 1; transform: scale(1) translateY(0); }
    }
    @keyframes offerMapDistanceFadeIn {
      0%   { opacity: 0; transform: translateY(4px); }
      100% { opacity: 1; transform: translateY(0); }
    }
  `;
  document.head.appendChild(style);
};

// ===================================================================
//  MAP STYLES
// ===================================================================

const mapStyles: google.maps.MapTypeStyle[] = [
  { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] },
  { featureType: "poi.business", stylers: [{ visibility: "off" }] },
  { featureType: "transit", elementType: "labels.icon", stylers: [{ visibility: "off" }] },
];

// ===================================================================
//  DISTANCE LABEL (frosted pill at midpoint)
// ===================================================================

const DistanceLabel: React.FC<{ position: { lat: number; lng: number }; distanceKm: number }> = React.memo(
  ({ position, distanceKm }) => {
    const pos = useMemo(() => ({ lat: position.lat, lng: position.lng }), [position.lat, position.lng]);
    const getPixelPositionOffset = useMemo(() => () => ({ x: -40, y: -20 }), []);

    const formattedDistance = distanceKm < 1
      ? `${Math.round(distanceKm * 1000)} m`
      : `${distanceKm.toFixed(1)} km`;

    return (
      <OverlayViewF
        position={pos}
        mapPaneName={OverlayView.FLOAT_PANE}
        getPixelPositionOffset={getPixelPositionOffset}
      >
        <div
          style={{
            backgroundColor: "rgba(255,255,255,0.92)",
            backdropFilter: "blur(10px)",
            borderRadius: "20px",
            padding: "6px 16px",
            boxShadow: "0 2px 12px rgba(0,0,0,0.1), 0 0 0 1px rgba(255,255,255,0.6) inset",
            border: "1px solid rgba(0,0,0,0.06)",
            pointerEvents: "none",
            animation: "offerMapDistanceFadeIn 0.3s ease 0.3s both",
          }}
        >
          <span
            style={{
              fontFamily: dt.fontMono,
              fontSize: "13px",
              fontWeight: 600,
              color: dt.textPrimary,
              whiteSpace: "nowrap",
            }}
          >
            {formattedDistance}
          </span>
        </div>
      </OverlayViewF>
    );
  }
);

// ===================================================================
//  MAIN COMPONENT
// ===================================================================

const OfferMapModal: React.FC<OfferMapModalProps> = ({
  open,
  onClose,
  mapData,
  viewerLocation,
}) => {
  const { translate } = useTranslation(["serviceOffers", "serviceBoard"]);
  const mapRef = useRef<google.maps.Map | null>(null);
  const [mapReady, setMapReady] = useState(false);

  const apiKey = (window as any).app?.config?.googleMapsApiKey || "";
  const { isLoaded } = useJsApiLoader({
    id: GOOGLE_MAPS_SCRIPT_ID,
    googleMapsApiKey: apiKey,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const counterpartPosition = useMemo(
    () => ({ lat: mapData.counterpartLat, lng: mapData.counterpartLng }),
    [mapData.counterpartLat, mapData.counterpartLng]
  );

  const hasBothLocations = viewerLocation != null;

  // Distance between the two markers
  const distanceKm = useMemo(() => {
    if (!viewerLocation) return null;
    return computeDistanceKm(
      mapData.counterpartLat,
      mapData.counterpartLng,
      viewerLocation.lat,
      viewerLocation.lng
    );
  }, [mapData.counterpartLat, mapData.counterpartLng, viewerLocation]);

  // Midpoint for distance label
  const midpoint = useMemo(() => {
    if (!viewerLocation) return null;
    return {
      lat: (mapData.counterpartLat + viewerLocation.lat) / 2,
      lng: (mapData.counterpartLng + viewerLocation.lng) / 2,
    };
  }, [mapData.counterpartLat, mapData.counterpartLng, viewerLocation]);

  const showOnMapDistance = distanceKm != null && distanceKm >= 1;

  // Polyline path
  const polylinePath = useMemo(() => {
    if (!viewerLocation) return null;
    return [counterpartPosition, viewerLocation];
  }, [counterpartPosition, viewerLocation]);

  // Distance text for header
  const headerDistanceText = useMemo(() => {
    if (distanceKm == null) return null;
    if (distanceKm < 1) return `${Math.round(distanceKm * 1000)} m`;
    return `${distanceKm.toFixed(1)} km`;
  }, [distanceKm]);

  useEffect(() => {
    injectDialogKeyframes();
  }, []);

  const handleMapLoad = useCallback(
    (map: google.maps.Map) => {
      mapRef.current = map;

      if (hasBothLocations && viewerLocation) {
        const bounds = new google.maps.LatLngBounds();
        bounds.extend(counterpartPosition);
        bounds.extend(viewerLocation);
        map.fitBounds(bounds, { top: 60, bottom: 60, left: 60, right: 60 });
      } else {
        map.setCenter(counterpartPosition);
        map.setZoom(14);
      }

      setTimeout(() => setMapReady(true), 100);
    },
    [hasBothLocations, viewerLocation, counterpartPosition]
  );

  const handleClose = useCallback(() => {
    setMapReady(false);
    onClose();
  }, [onClose]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          borderRadius: "20px",
          overflow: "hidden",
          boxShadow: "0 24px 80px rgba(0,0,0,0.12), 0 8px 24px rgba(0,0,0,0.06)",
          border: "1px solid rgba(255,255,255,0.8)",
          animation: "offerMapDialogEnter 0.3s cubic-bezier(0.16,1,0.3,1) both",
          width: "900px",
          maxWidth: "95vw",
        },
      }}
    >
      {/* Blue accent strip */}
      <Box
        sx={{
          height: "4px",
          width: "100%",
          background: `linear-gradient(90deg, ${dt.blue}, ${dt.blue}80)`,
          borderRadius: "20px 20px 0 0",
          flexShrink: 0,
        }}
      />

      {/* Header */}
      <Box
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          padding: "20px 24px 16px 24px",
          background: `linear-gradient(135deg, ${dt.blue}08 0%, transparent 60%)`,
          borderBottom: `1px solid ${dt.cardBorder}`,
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3, minWidth: 0, flex: 1, pr: 8 }}>
          <Box
            sx={{
              fontFamily: dt.font,
              fontSize: "18px",
              fontWeight: 700,
              color: dt.textPrimary,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {mapData.counterpartName}
          </Box>

          {(mapData.counterpartCity || headerDistanceText) && (
            <Box
              sx={{
                fontFamily: dt.fontMono,
                fontSize: "13px",
                fontWeight: 600,
                color: dt.blue,
              }}
            >
              {mapData.counterpartCity || ""}
              {mapData.counterpartCity && headerDistanceText ? " \u00B7 " : ""}
              {headerDistanceText || ""}
            </Box>
          )}
        </Box>

        {/* Offer number */}
        <Box
          sx={{
            position: "absolute",
            top: "20px",
            right: "56px",
            fontFamily: dt.fontMono,
            fontSize: "12px",
            fontWeight: 500,
            color: dt.textMuted,
          }}
        >
          {mapData.offerNumber}
        </Box>

        {/* Close button */}
        <Box
          onClick={handleClose}
          sx={{
            position: "absolute",
            top: "12px",
            right: "12px",
            width: 28,
            height: 28,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            opacity: 0.35,
            transition: "opacity 0.2s ease, background-color 0.2s ease",
            fontSize: "16px",
            color: dt.textPrimary,
            fontWeight: 300,
            userSelect: "none",
            "&:hover": {
              opacity: 0.8,
              backgroundColor: "rgba(0,0,0,0.04)",
            },
          }}
        >
          {"\u00D7"}
        </Box>
      </Box>

      {/* Map area */}
      <Box sx={{ width: "100%", height: 520, position: "relative" }}>
        {isLoaded ? (
          <>
            {/* Top gradient overlay */}
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "40px",
                background: "linear-gradient(180deg, rgba(255,255,255,0.5) 0%, transparent 100%)",
                pointerEvents: "none",
                zIndex: 1,
              }}
            />

            <GoogleMap
              mapContainerStyle={{ width: "100%", height: "100%" }}
              center={counterpartPosition}
              zoom={14}
              onLoad={handleMapLoad}
              options={{
                styles: mapStyles,
                disableDefaultUI: false,
                zoomControl: true,
                streetViewControl: false,
                mapTypeControl: false,
                fullscreenControl: false,
                gestureHandling: "greedy",
              }}
            >
              {/* Counterpart marker */}
              <RfsOwnerMarker
                position={counterpartPosition}
                companyName={mapData.counterpartName}
                logoUrl={mapData.counterpartLogoUrl}
                priorityCode={3}
              />

              {/* Home (viewer) marker */}
              {viewerLocation && (
                <HomeMarker position={viewerLocation} />
              )}

              {/* Distance line */}
              {polylinePath && (
                <PolylineF
                  path={polylinePath}
                  options={{
                    strokeColor: "#3498DB",
                    strokeOpacity: 0.5,
                    strokeWeight: 2,
                    geodesic: true,
                  }}
                />
              )}

              {/* Distance label */}
              {mapReady && showOnMapDistance && midpoint && distanceKm != null && (
                <DistanceLabel position={midpoint} distanceKm={distanceKm} />
              )}
            </GoogleMap>
          </>
        ) : (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: dt.inputBg,
            }}
          >
            <Box sx={{ fontSize: "13px", fontFamily: dt.font, color: dt.textMuted }}>
              {translate("serviceBoard:mapLoading")}
            </Box>
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

export default OfferMapModal;
